import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSplash } from '../../context/SplashContext';

const TICKER_DATA = [
  'SPX 5,842', 'EURUSD 1.092', 'VIX 14.32', 'UST10Y 4.28%',
  'GOLD 2,687', 'DXY 106.7', 'CRUDE 71.24', 'GBPUSD 1.263',
  'NIKKEI 38,642', 'DAX 19,215', 'BTC 97,420', 'ETH 3,412',
];

// Theme-aware: light base classes + dark: overrides (app default theme is dark).
const INPUT_CLS = 'login-input w-full rounded-lg px-3 py-2.5 lg:py-3 text-sm outline-none mb-3 '
  + 'bg-black/[0.03] border border-gray-300 text-gray-900 placeholder-gray-400 '
  + 'dark:bg-white/[0.06] dark:border-white/10 dark:text-white dark:placeholder-white/20';
const BTN_CLS = 'login-btn w-full py-3 lg:py-3.5 rounded-lg text-xs uppercase tracking-[3px] font-semibold '
  + 'cursor-pointer transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed relative overflow-hidden '
  + 'bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#dfc08a] '
  + 'lg:bg-transparent lg:text-gray-800 lg:border lg:border-gray-300 lg:hover:bg-black/[0.03] lg:hover:border-[#c9a96e] lg:hover:text-[#b18e4e] '
  + 'dark:lg:text-white dark:lg:border-white/15 dark:lg:hover:bg-white/5 dark:lg:hover:border-[#c9a96e] dark:lg:hover:text-[#c9a96e]';
const ERR_CLS = 'mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600 '
  + 'dark:bg-red-900/20 dark:border-red-800/30 dark:text-red-400';

const LoginPage = () => {
  const [username, setUsername]         = useState('');
  const [password, setPassword]         = useState('');
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [m, setM]                       = useState(false);

  // 2FA step
  const [step, setStep]                 = useState('credentials'); // 'credentials' | 'totp'
  const [partialToken, setPartialToken] = useState('');
  const [totpCode, setTotpCode]         = useState('');
  const totpRef                         = useRef(null);

  const { login, verify2fa } = useAuth();
  const { playSplash } = useSplash();
  const navigate = useNavigate();

  // login → enter: play the boot splash as the transition into the app.
  const enterApp = () => playSplash(() => navigate('/'), 2800);

  useEffect(() => { requestAnimationFrame(() => setM(true)); }, []);

  useEffect(() => {
    if (step === 'totp' && totpRef.current) {
      setTimeout(() => totpRef.current?.focus(), 100);
    }
  }, [step]);

  useEffect(() => {
    if (step === 'totp' && totpCode.replace(/\s/g, '').length === 6) {
      handleTotpSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totpCode]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result.requires_2fa) {
        setPartialToken(result.partial_token);
        setStep('totp');
      } else if (result.requires_2fa_setup) {
        navigate('/setup-2fa');
      } else {
        enterApp();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    if (e) e.preventDefault();
    const code = totpCode.replace(/\s/g, '');
    if (code.length < 6) return;
    setError('');
    setLoading(true);
    try {
      await verify2fa(partialToken, code);
      enterApp();
    } catch (err) {
      const status = err.response?.status;
      setError(
        status >= 500
          ? 'Server error — please try again in a moment.'
          : (err.response?.data?.error || 'Invalid code. Try again.')
      );
      setTotpCode('');
      setTimeout(() => totpRef.current?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const fade = (delay) => ({
    className: `transition-all duration-600 ease-out ${m ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`,
    style: { transitionDelay: `${delay}ms` },
  });

  return (
    <div className="flex flex-col lg:flex-row relative overflow-x-hidden bg-[#eef2f7] text-gray-900 dark:bg-[#0a0a0a] dark:text-white" style={{ height: '100dvh' }}>
      <div className="login-scanline" />
      <div className="login-grain" />

      {/* Floating tickers */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {TICKER_DATA.map((t, i) => (
          <span key={i} className="login-ticker" style={{
            left: `${(i * 8.3) % 100}%`,
            animationDelay: `${i * 1.7}s`,
            animationDuration: `${14 + (i % 5) * 3}s`,
          }}>{t}</span>
        ))}
      </div>

      {/* ========== LEFT PANEL (desktop) ========== */}
      <div className="flex-1 flex-col justify-between p-12 border-r border-gray-200 dark:border-white/5 hidden lg:flex relative z-10">
        <div {...fade(100)} className={`flex items-center gap-5 ${fade(100).className}`}>
          <div className="font-serif text-xl">Maghaweer<span className="text-[#c9a96e]">.ai</span></div>
          <div className="text-[10px] uppercase tracking-[3px] text-gray-500 dark:text-white/35 leading-tight">CAPITAL<br/>MARKETS<br/>INTELLIGENCE</div>
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-[480px]">
          <div {...fade(200)} className={`text-[10px] uppercase tracking-[4px] text-[#b18e4e] dark:text-[#c9a96e] mb-6 font-medium ${fade(200).className}`}>INTERNAL PLATFORM</div>
          <h1 className="font-serif text-5xl font-normal leading-[1.1] mb-8">
            {['Capital', 'markets'].map((w, i) => (
              <span key={w} {...fade(300 + i * 100)} className={`block ${fade(300 + i * 100).className}`}>{w}</span>
            ))}
            <em {...fade(550)} className={`block text-[#c9a96e] ${fade(550).className}`}>intelligence.</em>
          </h1>
          <p {...fade(650)} className={`text-sm text-gray-600 dark:text-white/40 leading-relaxed max-w-[380px] ${fade(650).className}`}>
            A consolidated intelligence environment for the capital markets team. Every tool, signal, and dataset &mdash; in one place.
          </p>
        </div>
        <div className="flex gap-10 pt-5">
          {['RESTRICTED\nACCESS', 'ENCRYPTED', 'SESSION\nMONITORED'].map((l, i) => (
            <div key={l} {...fade(900 + i * 150)} className={`text-[9px] uppercase tracking-[3px] text-gray-400 dark:text-white/25 font-medium whitespace-pre-line ${fade(900 + i * 150).className}`}>{l}</div>
          ))}
        </div>
      </div>

      {/* ========== RIGHT PANEL ========== */}
      <div
        className={`w-full lg:w-[520px] shrink-0 flex flex-col px-5 lg:px-14 py-4 lg:py-12 relative z-10 justify-between lg:justify-center transition-all duration-700 ease-out bg-white dark:bg-[#0f0f0f] ${
          m ? 'opacity-100 lg:translate-x-0' : 'opacity-0 lg:translate-x-12'
        }`}
        style={{ transitionDelay: '100ms' }}
      >
        {/* Mobile logo */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between">
            <div {...fade(150)} className={`font-serif text-[28px] ${fade(150).className}`}>
              Maghaweer<span className="text-[#c9a96e]">.ai</span>
            </div>
          </div>
          <div className="mt-3 mb-3">
            <h1 className="font-serif text-[36px] sm:text-[44px] leading-[1.1] font-normal">
              <span {...fade(300)} className={`block ${fade(300).className}`}>Capital markets</span>
              <em {...fade(450)} className={`block text-[#c9a96e] not-italic font-serif italic ${fade(450).className}`}>intelligence.</em>
            </h1>
          </div>
          <div {...fade(500)} className={`h-px bg-gray-200 dark:bg-white/10 mb-3 ${fade(500).className}`} />
        </div>

        {/* Auth section */}
        <div {...fade(550)} className={`flex items-center gap-2 text-[10px] uppercase tracking-[3px] text-[#b18e4e] dark:text-[#c9a96e] mb-3 lg:mb-10 font-medium ${fade(550).className}`}>
          <span className="login-dot w-1.5 h-1.5 rounded-full bg-[#c9a96e]" />
          AUTHORISED USERS ONLY
        </div>

        {step === 'credentials' ? (
          <>
            <h2 {...fade(600)} className={`font-serif text-xl lg:text-3xl font-normal mb-0.5 ${fade(600).className}`}>Sign in</h2>
            <p {...fade(650)} className={`text-[11px] lg:text-sm text-gray-500 dark:text-white/35 mb-4 lg:mb-10 ${fade(650).className}`}>Your session is end-to-end encrypted</p>

            {error && <div className={ERR_CLS}>{error}</div>}

            <form onSubmit={handleCredentials}>
              <label className="block text-[10px] uppercase tracking-[2px] text-gray-500 dark:text-white/45 font-medium mb-1">USERNAME</label>
              <input
                className={INPUT_CLS}
                type="text" value={username} onChange={e => setUsername(e.target.value)}
                required autoFocus placeholder="Enter username"
              />
              <label className="block text-[10px] uppercase tracking-[2px] text-gray-500 dark:text-white/45 font-medium mb-1">PASSWORD</label>
              <input
                className={`${INPUT_CLS} !mb-5`}
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="Enter password"
              />
              <button type="submit" disabled={loading} className={BTN_CLS}>
                {loading ? 'AUTHENTICATING...' : 'ACCESS PLATFORM'}
              </button>
            </form>

            <div {...fade(800)} className={`text-center mt-5 lg:mt-8 text-sm text-gray-500 dark:text-white/35 ${fade(800).className}`}>
              Don't have an account? <Link to="/register" className="text-[#b18e4e] dark:text-[#c9a96e] font-medium hover:text-[#c9a96e] dark:hover:text-[#dfc08a]">Request access</Link>
            </div>
          </>
        ) : (
          /* ── STEP 2: TOTP ── */
          <>
            <div className="mb-2">
              <h2 className="font-serif text-xl lg:text-3xl font-normal mb-0.5">Two-Factor Auth</h2>
              <p className="text-[11px] lg:text-sm text-gray-500 dark:text-white/35">
                Open your authenticator app and enter the 6-digit code for <span className="text-[#c9a96e]">Maghaweer.AI</span>.
              </p>
            </div>

            {error && <div className={ERR_CLS}>{error}</div>}

            <form onSubmit={handleTotpSubmit} className="mt-4">
              <label className="block text-[10px] uppercase tracking-[2px] text-gray-500 dark:text-white/45 font-medium mb-2">AUTHENTICATION CODE</label>
              <input
                ref={totpRef}
                className={`${INPUT_CLS} !mb-5 text-2xl text-center tracking-[0.5em] font-mono`}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="000000"
                autoComplete="one-time-code"
              />
              <button type="submit" disabled={loading || totpCode.length < 6} className={BTN_CLS}>
                {loading ? 'VERIFYING...' : 'VERIFY CODE'}
              </button>
            </form>

            <button
              onClick={() => { setStep('credentials'); setTotpCode(''); setError(''); }}
              className="mt-4 text-xs text-gray-400 hover:text-gray-600 dark:text-white/30 dark:hover:text-white/60 transition-colors text-center w-full"
            >
              ← Back to login
            </button>
          </>
        )}

        <p className="text-[10px] text-gray-400 dark:text-white/15 leading-relaxed hidden lg:block mt-6">
          All sessions are logged. Unauthorised access is strictly prohibited.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
